/* crm-ai-p4.jsx — AI contracts for Phase 4 communication features
   Provides: AIEmailDraftContract, AIMeetingSummaryContract, AICallSummaryContract,
             AIConversationSummary, AISuggestedReply, AISuggestedFollowUp, AICustomerSentiment
   Self-registers on window. */

const { useState, useEffect, useMemo, useRef, useCallback } = React;

/* ── shared helpers ── */
const _relDays = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return d + ' days ago';
  if (d < 30) return Math.floor(d / 7) + ' weeks ago';
  return Math.floor(d / 30) + ' months ago';
};

const _addBusinessDays = (days) => {
  const d = new Date();
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) added++;
  }
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
};

/* ── shared sub-components ── */
const AIBadge4 = () => {
  const store = useStore?.();
  return (
    <span className="nx-ai-compact-badge">
      {(store?.branding?.brandName || 'AI') + ' AI'}
    </span>
  );
};

const SkeletonLine4 = ({ width = '100%', height = 10 }) => (
  <div style={{ width, height, background: 'var(--nx-border)', borderRadius: 4, animation: 'pulse 1.5s infinite' }} />
);

const AICard4 = ({ icon = 'Sparkles', title, badge, children, loading, onRefresh, style = {} }) => (
  <div style={{ background: 'var(--nx-surface-alt)', border: '1px solid var(--nx-border)', borderRadius: 10, overflow: 'hidden', ...style }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--nx-border)', background: 'linear-gradient(135deg, #f8f7ff, #f0f4ff)' }}>
      <NexusIcon name={icon} size={13} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, fontSize: 12, flex: 1 }}>{title}</span>
      {badge && <AIBadge4 />}
      {onRefresh && !loading && (
        <button onClick={onRefresh} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--nx-accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4 }}>
          <NexusIcon name="RefreshCw" size={10} /> Regenerate
        </button>
      )}
    </div>
    <div style={{ padding: 12 }}>
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <SkeletonLine4 width="90%" /><SkeletonLine4 width="75%" /><SkeletonLine4 width="55%" />
        </div>
      ) : children}
    </div>
  </div>
);

const ToneBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{ padding: '4px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid ' + (active ? 'var(--nx-accent)' : 'var(--nx-border)'), background: active ? 'var(--nx-accent)' : 'transparent', color: active ? '#fff' : 'var(--nx-text-muted)' }}>{label}</button>
);

/* ══════════════════════════════════════════════════════════════
   AI EMAIL DRAFT CONTRACT
   ══════════════════════════════════════════════════════════════ */
const AIEmailDraftContract = ({ record, entityType, context = 'followup', onRequest, onFeedback }) => {
  const [loading, setLoading] = useState(false);
  const [tone, setTone] = useState('Formal');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const CONTEXT_LABELS = { reply: 'Reply', followup: 'Follow-up', intro: 'Introduction', proposal: 'Proposal' };
  const tones = ['Formal', 'Friendly', 'Brief'];

  const prefill = useCallback(() => {
    const name = record?.name || record?.title || 'there';
    const co   = record?.company || '';
    const prefixMap = {
      followup:  { s: `Follow-up: ${record?.title || name}`, b: `Hi ${name},\n\nI wanted to follow up on our last conversation${co ? ' regarding ' + co : ''}.\n\nPlease let me know if you have any questions or if there's anything I can help with.\n\nBest regards` },
      reply:     { s: `Re: ${record?.title || name}`, b: `Hi ${name},\n\nThank you for your message. I wanted to address your questions.\n\nBest regards` },
      intro:     { s: `Introduction — ${record?.title || co || name}`, b: `Hi ${name},\n\nI wanted to reach out to introduce myself and share how we might be able to help${co ? ' ' + co : ' your team'}.\n\nBest regards` },
      proposal:  { s: `Proposal for ${record?.title || co || name}`, b: `Hi ${name},\n\nThank you for the opportunity to submit a proposal. Please find our approach outlined below.\n\nBest regards` },
    };
    const p = prefixMap[context] || prefixMap.followup;
    setSubject(p.s);
    setBody(p.b);
  }, [record, context]);

  useEffect(() => { prefill(); }, [prefill]);

  const handleGenerate = () => {
    setLoading(true);
    onRequest?.({ type: 'email_draft', context, record, tone });
    setTimeout(() => setLoading(false), 800);
  };

  return (
    <AICard4 icon="Mail" title={`Email Draft — ${CONTEXT_LABELS[context] || context}`} badge loading={loading} onRefresh={handleGenerate}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {tones.map(t => <ToneBtn key={t} label={t} active={tone === t} onClick={() => setTone(t)} />)}
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600, marginBottom: 4 }}>SUBJECT</div>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', boxSizing: 'border-box' }} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', fontWeight: 600, marginBottom: 4 }}>BODY</div>
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={6}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={prefill} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Use Template</button>
          <button onClick={handleGenerate} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
            <NexusIcon name="Sparkles" size={11} /> Generate
          </button>
        </div>
      </div>
    </AICard4>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI MEETING SUMMARY CONTRACT
   ══════════════════════════════════════════════════════════════ */
const AIMeetingSummaryContract = ({ record, meeting: meetingProp, entityType, onRequest, onFeedback }) => {
  const meeting = record || meetingProp;
  const [loading, setLoading] = useState(false);
  const hasNotes = !!(meeting?.notes || meeting?.description);

  const summary = useMemo(() => {
    if (!hasNotes) return null;
    const notes = meeting.notes || meeting.description || '';
    return notes.length > 200 ? notes.slice(0, 200) + '…' : notes;
  }, [meeting, hasNotes]);

  const decisions = useMemo(() => {
    const outcomes = meeting?.outcomes || meeting?.decisions || [];
    if (Array.isArray(outcomes) && outcomes.length) return outcomes;
    if (typeof outcomes === 'string') return outcomes.split('\n').filter(Boolean);
    return [];
  }, [meeting]);

  const actions = useMemo(() => {
    const na = meeting?.nextActions || meeting?.actionItems || [];
    if (Array.isArray(na) && na.length) return na;
    if (typeof na === 'string') return na.split('\n').filter(Boolean);
    return [];
  }, [meeting]);

  const sentiment = useMemo(() => {
    const s = (meeting?.sentiment || '').toLowerCase();
    if (s === 'positive' || s === 'good') return 'Positive';
    if (s === 'negative' || s === 'bad') return 'Negative';
    return 'Neutral';
  }, [meeting]);

  const SENT_COLOR = { Positive: '#16A34A', Neutral: '#64748B', Negative: '#EF4444' };
  const SENT_BG    = { Positive: '#DCFCE7', Neutral: 'var(--nx-border)', Negative: '#FEE2E2' };

  const handleRegenerate = () => {
    setLoading(true);
    onRequest?.({ type: 'meeting_summary', meeting });
    setTimeout(() => setLoading(false), 600);
  };

  return (
    <AICard4 icon="Calendar" title="Meeting Summary" badge loading={loading} onRefresh={handleRegenerate}>
      {!hasNotes ? (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <NexusIcon name="FileText" size={20} style={{ color: 'var(--nx-text-muted)', marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: 'var(--nx-text-muted)', marginBottom: 8 }}>No notes captured yet</div>
          <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Add notes to the meeting record to enable summary generation.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Summary</div>
            <p style={{ fontSize: 13, color: 'var(--nx-text)', margin: 0, lineHeight: 1.6 }}>{summary}</p>
          </div>
          {decisions.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Key Decisions</div>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {decisions.map((d, i) => <li key={i} style={{ fontSize: 12, color: 'var(--nx-text)' }}>{d}</li>)}
              </ul>
            </div>
          )}
          {actions.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Action Items</div>
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                {actions.map((a, i) => <li key={i} style={{ fontSize: 12, color: 'var(--nx-text)', display: 'flex', alignItems: 'flex-start', gap: 6 }}><NexusIcon name="CheckSquare" size={11} style={{ color: 'var(--nx-accent)', flexShrink: 0, marginTop: 2 }} />{a}</li>)}
              </ul>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>Sentiment:</span>
            <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: SENT_BG[sentiment], color: SENT_COLOR[sentiment] }}>{sentiment}</span>
          </div>
        </div>
      )}
    </AICard4>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI CALL SUMMARY CONTRACT
   ══════════════════════════════════════════════════════════════ */
const AICallSummaryContract = ({ record, activity: activityProp, entityType, onRequest, onFeedback }) => {
  const activity = record || activityProp;
  const [loading, setLoading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState(false);
  const [nextAction, setNextAction] = useState('');

  const summary = activity?.what || activity?.description || '';

  const nextActions = ['Send proposal', 'Schedule follow-up', 'Loop in decision maker', 'Send contract', 'Do nothing'];

  const handleCapture = () => {
    setCapturing(false);
    onRequest?.({ type: 'call_notes', activity, notes, followUp, nextAction });
  };

  return (
    <AICard4 icon="Phone" title="Call Summary" badge loading={loading}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {summary && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Summary</div>
            <p style={{ fontSize: 13, color: 'var(--nx-text)', margin: 0, lineHeight: 1.6 }}>{summary}</p>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Follow-up needed?</span>
          <button onClick={() => setFollowUp(f => !f)} style={{
            width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
            background: followUp ? 'var(--nx-accent)' : 'var(--nx-border)', position: 'relative', transition: 'background 0.2s',
          }}>
            <span style={{ position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: followUp ? 21 : 3 }} />
          </button>
          <span style={{ fontSize: 12, color: followUp ? 'var(--nx-accent)' : 'var(--nx-text-muted)', fontWeight: followUp ? 700 : 400 }}>{followUp ? 'Yes' : 'No'}</span>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Suggested Next Action</div>
          <select value={nextAction} onChange={e => setNextAction(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)' }}>
            <option value="">— Select action —</option>
            {nextActions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {capturing ? (
          <div>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="Add call notes here…"
              style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={() => setCapturing(false)} style={{ padding: '5px 12px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 12, color: 'var(--nx-text-muted)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleCapture} style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save Notes</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setCapturing(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 13, color: 'var(--nx-accent)', cursor: 'pointer', fontWeight: 600 }}>
            <NexusIcon name="Edit3" size={13} /> Capture Notes
          </button>
        )}
      </div>
    </AICard4>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI CONVERSATION SUMMARY
   ══════════════════════════════════════════════════════════════ */
const AIConversationSummary = ({ record, type, entityType, onRequest, onFeedback }) => {
  const effectiveType = entityType || type;
  const store = useStore();

  const stats = useMemo(() => {
    if (!record) return { total: 0, lastDate: null, topics: [] };
    let scopeKey = 'personId';
    if (effectiveType === 'deal') scopeKey = 'dealId';
    else if (effectiveType === 'company') scopeKey = 'companyId';
    const acts = (store?.activity || []).filter(a => a[scopeKey] === record.id || a.contactId === record.id);
    const lastAct = acts.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    const topics = [...new Set(acts.map(a => (a.what || a.type || '').split(' ').slice(0, 3).join(' ')).filter(Boolean))].slice(0, 3);
    return { total: acts.length, lastDate: lastAct ? (lastAct.createdAt || lastAct.date) : null, topics };
  }, [store, record, effectiveType]);

  const health = useMemo(() => {
    if (!stats.lastDate) return { label: 'Cold', color: '#EF4444', bg: '#FEE2E2' };
    const days = Math.floor((Date.now() - new Date(stats.lastDate).getTime()) / 86400000);
    if (days <= 7) return { label: 'Active', color: '#16A34A', bg: '#DCFCE7' };
    if (days <= 30) return { label: 'Warm', color: '#F59E0B', bg: '#FEF9C3' };
    return { label: 'Cold', color: '#EF4444', bg: '#FEE2E2' };
  }, [stats]);

  return (
    <AICard4 icon="MessageSquare" title="Conversation Summary" badge onRefresh={() => onRequest?.({ type: 'conversation_summary', record })}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Last Contacted</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{stats.lastDate ? _relDays(stats.lastDate) : 'Never'}</div>
          </div>
          <div style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginBottom: 4 }}>Total Interactions</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-text)' }}>{stats.total}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>Communication Health:</span>
          <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: health.bg, color: health.color }}>{health.label}</span>
        </div>
        {stats.topics.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6 }}>KEY TOPICS</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {stats.topics.map((t, i) => (
                <span key={i} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', color: 'var(--nx-text-muted)' }}>{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </AICard4>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI SUGGESTED REPLY
   ══════════════════════════════════════════════════════════════ */
const AISuggestedReply = ({ record, email: emailProp, entityType, onRequest, onFeedback, onUse }) => {
  const email = record || emailProp;
  const [tone, setTone] = useState('Formal');
  const tones = ['Formal', 'Friendly', 'Brief'];

  const draft = useMemo(() => {
    const from = email?.from || email?.sender || 'them';
    const sub  = email?.subject || 'your message';
    return `Hi ${from},\n\nThank you for reaching out regarding ${sub}. I wanted to follow up and address your points.\n\nPlease let me know if you have any additional questions.\n\nBest regards`;
  }, [email]);

  const [text, setText] = useState(draft);
  useEffect(() => { setText(draft); }, [draft]);

  return (
    <AICard4 icon="Reply" title="Suggested Reply" badge onRefresh={() => onRequest?.({ type: 'suggested_reply', email })}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {tones.map(t => <ToneBtn key={t} label={t} active={tone === t} onClick={() => setTone(t)} />)}
        </div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={5}
          style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1px solid var(--nx-border)', fontSize: 13, background: 'var(--nx-surface)', color: 'var(--nx-text)', resize: 'vertical', boxSizing: 'border-box' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={() => onRequest?.({ type: 'improve_reply', email, tone })} style={{ padding: '6px 12px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 12, color: 'var(--nx-accent)', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}>
            <NexusIcon name="Sparkles" size={11} /> Improve
          </button>
          <button onClick={() => onUse?.(text)} style={{ padding: '6px 12px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Use This Reply</button>
        </div>
      </div>
    </AICard4>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI SUGGESTED FOLLOW-UP
   ══════════════════════════════════════════════════════════════ */
const AISuggestedFollowUp = ({ record, type, entityType, onRequest, onFeedback }) => {
  const effectiveType = entityType || type;
  const store = useStore();

  const suggestion = useMemo(() => {
    const acts = (store?.activity || []).filter(a => {
      if (effectiveType === 'deal')    return a.dealId === record?.id;
      if (effectiveType === 'company') return a.companyId === record?.id;
      return a.personId === record?.id || a.contactId === record?.id;
    });
    const last = acts.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    const days = last ? Math.floor((Date.now() - new Date(last.createdAt || 0).getTime()) / 86400000) : 999;
    if (days > 14) return { actionType: 'CALL', subject: 'Re-engage — schedule catch-up call', reason: 'No activity in 14+ days' };
    if (!record?.closeDate) return { actionType: 'EMAIL', subject: 'Confirm timeline and next steps', reason: 'No close date set' };
    return { actionType: 'EMAIL', subject: `Follow-up: ${record?.title || record?.name || 'your inquiry'}`, reason: 'Routine follow-up' };
  }, [store, record, effectiveType]);

  const suggestedDate = _addBusinessDays(3);

  const createTask = () => {
    const params = { title: suggestion.subject };
    if (effectiveType === 'deal')    params.dealId    = record?.id;
    if (effectiveType === 'company') params.companyId = record?.id;
    if (effectiveType === 'person')  params.personId  = record?.id;
    store?.openCreateForm?.('tasks', params);
  };

  const scheduleMeeting = () => {
    if (typeof window.ScheduleMeetingModal !== 'undefined') {
      window.ScheduleMeetingModal?.open?.({ record, type: effectiveType });
    } else {
      store?.openCreateForm?.('tasks', { title: 'Schedule meeting: ' + (record?.name || record?.title || '') });
    }
  };

  const ACTION_ICON = { CALL: 'Phone', EMAIL: 'Mail', MEETING: 'Calendar' };
  const ACTION_COLOR = { CALL: '#0EA5E9', EMAIL: '#2563EB', MEETING: '#7C3AED' };
  const ac = ACTION_COLOR[suggestion.actionType] || 'var(--nx-accent)';

  return (
    <AICard4 icon="Compass" title="Suggested Follow-Up" badge onRefresh={() => onRequest?.({ type: 'suggested_followup', record })}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--nx-surface)', border: '1px solid var(--nx-border)', borderRadius: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: ac + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <NexusIcon name={ACTION_ICON[suggestion.actionType] || 'Activity'} size={15} style={{ color: ac }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nx-text)' }}>{suggestion.subject}</div>
            <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{suggestion.reason}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--nx-text-muted)' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><NexusIcon name="Calendar" size={12} /> {suggestedDate}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><NexusIcon name="Tag" size={12} /> {suggestion.actionType}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={createTask} style={{ flex: 1, padding: '7px 12px', borderRadius: 7, border: '1px solid var(--nx-border)', background: 'transparent', fontSize: 12, color: 'var(--nx-accent)', cursor: 'pointer', fontWeight: 600 }}>Create Task</button>
          {suggestion.actionType === 'MEETING' && (
            <button onClick={scheduleMeeting} style={{ flex: 1, padding: '7px 12px', borderRadius: 7, border: 'none', background: 'var(--nx-accent)', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Schedule Meeting</button>
          )}
        </div>
      </div>
    </AICard4>
  );
};

/* ══════════════════════════════════════════════════════════════
   AI CUSTOMER SENTIMENT (heuristic — no LLM)
   ══════════════════════════════════════════════════════════════ */
const AICustomerSentiment = ({ record, type, entityType, onRequest, onFeedback }) => {
  const effectiveType = entityType || type;
  const store = useStore();

  const sentiment = useMemo(() => {
    if (!record) return { label: 'Unknown', color: '#64748B', icon: 'HelpCircle' };
    let scopeKey = 'personId';
    if (effectiveType === 'deal') scopeKey = 'dealId';
    else if (effectiveType === 'company') scopeKey = 'companyId';
    const acts = (store?.activity || []).filter(a => a[scopeKey] === record.id || a.contactId === record.id);
    const tasks = (store?.tasks || []).filter(t => t[scopeKey] === record.id && !t.completedAt);
    const lastAct = acts.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))[0];
    const daysSince = lastAct ? Math.floor((Date.now() - new Date(lastAct.createdAt || 0).getTime()) / 86400000) : 999;

    if (daysSince > 30) return { label: 'Cold', color: '#EF4444', icon: 'AlertCircle', detail: 'No activity in 30+ days' };
    if (tasks.length > 5) return { label: 'At Risk', color: '#F59E0B', icon: 'AlertTriangle', detail: `${tasks.length} open tasks outstanding` };
    if (effectiveType === 'deal') {
      const stage = (store?.pipeline || []).find(s => s.id === record.stageId);
      if (stage && (stage.probability < 30 || /lost/i.test(stage.name))) return { label: 'At Risk', color: '#F59E0B', icon: 'AlertTriangle', detail: 'Low win probability stage' };
    }
    if (daysSince <= 7) return { label: 'Active', color: '#16A34A', icon: 'CheckCircle', detail: 'Recent activity within last 7 days' };
    return { label: 'Warm', color: '#22C55E', icon: 'TrendingUp', detail: 'Engagement within last 30 days' };
  }, [store, record, effectiveType]);

  const TRAFFIC = { Active: '#22C55E', Warm: '#22C55E', 'At Risk': '#F59E0B', Cold: '#EF4444', Unknown: '#64748B' };

  return (
    <AICard4 icon="Gauge" title="Customer Sentiment">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Traffic light */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
            {['Active', 'At Risk', 'Cold'].map(lvl => (
              <div key={lvl} style={{ width: 20, height: 20, borderRadius: '50%', background: sentiment.label === lvl ? TRAFFIC[lvl] : 'var(--nx-border)', transition: 'background 0.3s', boxShadow: sentiment.label === lvl ? `0 0 8px ${TRAFFIC[lvl]}` : 'none' }} />
            ))}
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: sentiment.color }}>{sentiment.label}</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{sentiment.detail}</div>
          </div>
        </div>
        <div style={{ padding: '8px 10px', background: '#FEF9C3', border: '1px solid #FDE047', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Sparkles" size={12} style={{ color: '#A16207', flexShrink: 0 }} />
          <span style={{ fontSize: 11, color: '#A16207', fontWeight: 600 }}>AI Powered — Coming Soon. Sentiment is currently heuristic-based.</span>
        </div>
      </div>
    </AICard4>
  );
};

/* ── registrations ── */
window.AIEmailDraftContract = AIEmailDraftContract;
window.AIMeetingSummaryContract = AIMeetingSummaryContract;
window.AICallSummaryContract = AICallSummaryContract;
window.AIConversationSummary = AIConversationSummary;
window.AISuggestedReply = AISuggestedReply;
window.AISuggestedFollowUp = AISuggestedFollowUp;
window.AICustomerSentiment = AICustomerSentiment;
